import { Instance, types } from 'mobx-state-tree';
import { AddressEntryFormData } from '../address/main-address/AddressEntryFormData';
import { ShippingMethod } from '../shipping/method-selection/ShippingMethodSelectionStore';
import { getRootStore } from '../root/RootStoreUtils';
import type { PickupPointModalResult, PickupPointModalOptions } from 'yl-pickup-point';

export type ShowPickupPointResult = AddressEntryFormData | undefined;

const DEFAULT_MAX_STREET_LENGTH = 40;

export const PickupPointModalStore = types
  .model({
    modalOptions: types.maybe(types.frozen<PickupPointModalOptions>())
  })
  .views(self => ({
    get availableShippingMethods(): ShippingMethod[] | undefined {
      return getRootStore(self).moduleStores?.shippingMethodSelection?.shippingMethods;
    },
    get locale(): string {
      return getRootStore(self).localeManager.language.locale;
    }
  }))
  .volatile(() => ({
    pickupPointModalDeferred: undefined as ((value: AddressEntryFormData | undefined) => void) | undefined
  }))
  .actions(self => {
    const extractFieldLimits = () => {
      const rootStore = getRootStore(self);
      const addressEntryForm = rootStore.moduleStores.addressEntry?.form ||
                              rootStore.moduleStores.shippingAddressEntry?.form;

      const street1Field = addressEntryForm?.rows
        ?.flatMap(row => row.formFields)
        ?.find(field => field.name === 'street1');
      const street2Field = addressEntryForm?.rows
        ?.flatMap(row => row.formFields)
        ?.find(field => field.name === 'street2');

      let street1MaxLength = street1Field?.maxLength;
      let street2MaxLength = street2Field?.maxLength;

      if (street1MaxLength == null || street1MaxLength == undefined || street1MaxLength > DEFAULT_MAX_STREET_LENGTH) {
          street1MaxLength = DEFAULT_MAX_STREET_LENGTH;
      }

      if (street2MaxLength == null || street2MaxLength == undefined || street2MaxLength > DEFAULT_MAX_STREET_LENGTH) {
          street2MaxLength = DEFAULT_MAX_STREET_LENGTH;
      }

      return { street1MaxLength, street2MaxLength };
    };

    const findOptimalSplitPoint = (fullAddress: string, maxLength: number) => {
      let lastSpaceIndex = -1;
      for (let i = 0; i <= maxLength && i < fullAddress.length; i++) {
          if (fullAddress[i] === ' ' || fullAddress[i] === ',') {
              lastSpaceIndex = i;
          }
      }

      if (lastSpaceIndex === -1) {
        return maxLength;
      } else {
        return lastSpaceIndex;
      }
    };

    const performAddressSplit = (fullAddress: string, splitIndex: number, street2MaxLength: number) => {
      const newStreet1 = fullAddress.substring(0, splitIndex).trim();
      const remainingText = fullAddress.substring(splitIndex).trim();

      let newStreet2 = remainingText;
      if (newStreet2.length > street2MaxLength) {
        newStreet2 = newStreet2.substring(0, street2MaxLength).trim();
      }

      return { street1: newStreet1, street2: newStreet2 };
    };

    return {
      splitStreetAddressIfNeeded(street1: string, street2 = ''): { street1: string; street2: string } {

        if (!street1) {
          return { street1, street2 };
        }

        const { street1MaxLength, street2MaxLength } = extractFieldLimits();

        const street1ExceedsLimit = street1.length > street1MaxLength;
        const street2ExceedsLimit = street2 && street2.length > street2MaxLength;

        if (!street1ExceedsLimit && !street2ExceedsLimit) {
          return { street1, street2 };
        }

        const fullAddress = street2 ? `${street1} ${street2}` : street1;

        const splitIndex = findOptimalSplitPoint(fullAddress, street1MaxLength);
        return performAddressSplit(fullAddress, splitIndex, street2MaxLength);
      },
    showPickupPointModal(): Promise<ShowPickupPointResult> {
      const rootStore = getRootStore(self);
      const shippingAddressEntry = rootStore.moduleStores.shippingAddressEntry;
      const phoneValidation = shippingAddressEntry?.phoneValidation;
      const sourceAddress = shippingAddressEntry?.pickupPointAddress || shippingAddressEntry?.userShippingAddress;

      self.modalOptions = {
        locale: self.locale,
        shippingMethod: rootStore.moduleStores.shippingMethodSelection!.selectedShippingMethod!,
        phoneOptions:
          !phoneValidation || phoneValidation.internationalMode
            ? {
                internationalMode: true,
                defaultCountryIsoCode: rootStore.enrollmentStatus.countryIso
              }
            : {
                internationalMode: false,
                validationPattern: phoneValidation.phoneRegularExpression
              },
        shippingAddress: sourceAddress && {
          houseNumber: sourceAddress.houseNumber,
          interiorNumber: sourceAddress.interiorNumber,
          neighborhood: sourceAddress.neighborhood,
          countryIsoCode: sourceAddress.country || '',
          city: sourceAddress.city || '',
          stateProv: sourceAddress.stateProv,
          postalCode: sourceAddress.postalCode || '',
          street1: sourceAddress.street1 || '',
          street2: sourceAddress.street2 || '',
          street3: sourceAddress.street3 || '',
          street4: '',
          phoneNumber: sourceAddress.phoneNumber,
          recipient: sourceAddress.recipient
        }
      };

      return new Promise<AddressEntryFormData | undefined>(resolve => {
        self.pickupPointModalDeferred = resolve;
      });
    },
    closePickupPointModal(result: PickupPointModalResult | undefined): void {
      self.modalOptions = undefined;
      if (self.pickupPointModalDeferred) {
        if (result) {
          // Check and split street1 if needed before creating the address
          const { street1, street2 } = this.splitStreetAddressIfNeeded(
            result.address.street1 || '', 
            result.address.street2 || ''
          );

          const address = AddressEntryFormData.create({
            city: result.address.city,
            country: result.address.countryIsoCode2,
            postalCode: result.address.postalCode,
            stateProv: result.address.stateProv,
            street1: street1,
            street2: street2,
            street3: result.address.street3,
            phoneNumber: result.address.phoneNumber,
            recipient: result.address.recipient,
            isWillCall: false,
            isPickupPoint: true,
            isAusPost: false,
            isValidated: true
          });

          self.pickupPointModalDeferred(address);
        } else {
          self.pickupPointModalDeferred(undefined);
        }
      }
      self.pickupPointModalDeferred = undefined;
    }
    };
  });

export type PickupPointModalStore = Instance<typeof PickupPointModalStore>;
